import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, Check, X, Info } from "lucide-react";

export const passwordChecks = (pw) => [
  { ok: pw.length >= 12, label: "Mindst 12 tegn" },
  { ok: pw.length <= 128, label: "Højst 128 tegn" },
  { ok: true, label: "Må ikke findes i kendte datalæk (tjekkes ved gem)", info: true },
];

export const passwordValid = (pw) => passwordChecks(pw).every((c) => c.ok);

const strengthOf = (pw) => {
  let s = 0;
  if (pw.length >= 12) s++;
  if (pw.length >= 16) s++;
  if (pw.length >= 20) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw) || /[^A-Za-z0-9]/.test(pw)) s++;
  if (new Set(pw).size >= 10) s++;
  if (pw.length < 12) return 1;
  return Math.max(1, Math.min(4, Math.round((s / 6) * 4)));
};
const LEVELS = ["", "Svag", "Middel", "God", "Stærk"];
const COLORS = ["#EBE8E3", "#C0392B", "#D68910", "#7FA85A", "#2E7D32"];

export const PasswordField = ({ value, onChange, placeholder = "Ny adgangskode", testid = "password-input", showRules = true, autoFocus = false }) => {
  const [show, setShow] = useState(false);
  const checks = useMemo(() => passwordChecks(value), [value]);
  const strength = value ? strengthOf(value) : 0;
  return (
    <div className="space-y-2">
      <div className="relative">
        <Input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus}
          data-testid={testid} autoComplete="new-password" className="h-11 rounded-xl bg-white pr-11" />
        <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Skjul" : "Vis"} data-testid={`${testid}-toggle`} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {showRules && (
        <>
          <div className="flex items-center gap-2" data-testid={`${testid}-strength`}>
            <div className="flex-1 flex gap-1">
              {[1, 2, 3, 4].map((i) => <span key={i} className="h-1.5 flex-1 rounded-full [transition-property:background-color] duration-300" style={{ background: i <= strength ? COLORS[strength] : COLORS[0] }} />)}
            </div>
            <span className="text-xs w-12 text-right" style={{ color: strength ? COLORS[strength] : "#A69B8B" }}>{LEVELS[strength]}</span>
          </div>
          <ul className="grid grid-cols-1 gap-y-1" data-testid={`${testid}-rules`}>
            {checks.map((c) => (
              <li key={c.label} className={`flex items-center gap-1.5 text-xs ${c.info ? "text-stone-500" : c.ok ? "text-[#2E7D32]" : "text-stone-500"}`}>
                {c.info ? <Info className="h-3.5 w-3.5 text-stone-400" /> : c.ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5 text-stone-300" />} {c.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
